/**
 * VerificationListItem — a single row in the Admin Verifications directory.
 * Pure presentational: every action is a handler passed in from the parent.
 */
import {
  BadgeCheck, ShieldCheck, ShieldX, MapPin, Mail, ExternalLink,
  Loader2, Trash2, Image as ImageIcon, CheckSquare, Square,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import BusinessLogo from "@/components/BusinessLogo";
import BulkProductUpload from "@/components/BulkProductUpload";
import AdminLogoUploadButton from "@/components/AdminLogoUploadButton";
import AdminDMCopyButton from "@/components/AdminDMCopyButton";

function PingDot({ business, ping }) {
  if (!business.website) {
    return (
      <span
        title="No website on file"
        className="inline-flex w-2 h-2 rounded-full bg-gray-300"
        data-testid={`ping-dot-${business.business_id}`}
        data-ping-state="no-url"
      />
    );
  }
  if (!ping) {
    return (
      <span
        title="Checking site…"
        className="inline-flex w-2 h-2 rounded-full bg-gray-300 animate-pulse"
        data-testid={`ping-dot-${business.business_id}`}
        data-ping-state="checking"
      />
    );
  }
  const color = ping.ok ? "bg-green-500" : "bg-red-500";
  const label = ping.ok
    ? `Site reachable (${ping.status})`
    : ping.error === "no_url"
    ? "No website on file"
    : ping.error === "timeout"
    ? "Site timed out"
    : `Site unreachable${ping.status ? ` (${ping.status})` : ""}`;
  return (
    <span
      title={label}
      className={`inline-flex w-2 h-2 rounded-full ${color}`}
      data-testid={`ping-dot-${business.business_id}`}
      data-ping-state={ping.ok ? "ok" : "fail"}
    />
  );
}

export default function VerificationListItem({
  business: b,
  status,
  selected,
  onToggleSelect,
  isFocused,
  rowRef,
  ping,
  busy,
  onVerify,
  onUnverify,
  onDelete,
  onRefreshLogo,
  onLogoUploaded,
}) {
  return (
    <div
      ref={rowRef}
      className={`border rounded-lg overflow-hidden bg-white transition-shadow ${
        isFocused ? "border-tbc-gold ring-2 ring-tbc-gold/40 shadow-md" : "border-gray-200"
      }`}
      data-testid={`admin-verification-row-${b.business_id}`}
    >
      <div className="flex">
        {status === "pending" && !b.is_verified && (
          <button
            onClick={() => onToggleSelect(b.business_id)}
            className="w-8 flex items-center justify-center border-r border-gray-100 hover:bg-tbc-cream/50"
            aria-label={selected ? "Deselect" : "Select"}
            data-testid={`select-biz-${b.business_id}`}
          >
            {selected ? <CheckSquare className="w-4 h-4 text-tbc-navy" /> : <Square className="w-4 h-4 text-gray-300" />}
          </button>
        )}
        <div className="w-32 h-32 bg-tbc-cream/40 flex-shrink-0 flex items-center justify-center p-2">
          <BusinessLogo name={b.name} logoUrl={b.logo_url} size="lg" shape="rounded" />
        </div>
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-heading text-lg text-tbc-navy truncate">{b.name}</h3>
                <PingDot business={b} ping={ping} />
                {b.is_verified && (
                  <BadgeCheck className="w-4 h-4 text-tbc-gold flex-shrink-0" data-testid={`admin-verified-icon-${b.business_id}`} />
                )}
                {b.source === "google_places" && (
                  <span className="text-[10px] uppercase tracking-wider text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                    Google Places
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {b.category === "Other" && b.custom_category ? b.custom_category : b.category}
              </p>
            </div>
            <a
              href={`/business/${b.business_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-tbc-navy p-1"
              title="View public profile"
              data-testid={`admin-view-business-${b.business_id}`}
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          <p className="text-xs text-gray-600 line-clamp-2 mt-2">{b.description}</p>

          <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500">
            {b.city && b.state && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {b.city}, {b.state}
              </span>
            )}
            {b.owner?.email && (
              <span className="inline-flex items-center gap-1">
                <Mail className="w-3 h-3" />
                {b.owner.email}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {b.is_verified ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onUnverify(b)}
                disabled={busy}
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid={`admin-unverify-${b.business_id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldX className="w-3 h-3 mr-1" />}
                Un-verify
              </Button>
            ) : (
              <Button
                size="sm"
                onClick={() => onVerify(b)}
                disabled={busy}
                className="bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                data-testid={`admin-verify-${b.business_id}`}
              >
                {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                Verify
              </Button>
            )}
            <BulkProductUpload
              businessId={b.business_id}
              businessName={b.name}
              triggerLabel="Products"
              buttonClassName="h-8 text-xs px-2 border-tbc-navy text-tbc-navy hover:bg-tbc-navy/5"
            />
            {!b.is_claimed && <AdminDMCopyButton business={b} />}
            <AdminLogoUploadButton
              businessId={b.business_id}
              businessName={b.name}
              label="Upload"
              onUploaded={onLogoUploaded}
              className="h-8 text-xs px-2"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => onRefreshLogo(b)}
              disabled={busy}
              className="h-8 text-xs px-2"
              title={b.website ? "Re-fetch logo from this business's website" : "No website on file"}
              data-testid={`admin-refresh-logo-${b.business_id}`}
            >
              {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <ImageIcon className="w-3 h-3 mr-1" />}
              Auto-fetch
            </Button>
            {b.website && (
              <a
                href={b.website}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 h-8 text-xs px-2 rounded-md border border-gray-200 text-tbc-navy hover:bg-tbc-navy/5"
                title="Open this business's website in a new tab"
                data-testid={`admin-visit-site-${b.business_id}`}
              >
                <ExternalLink className="w-3 h-3" />
                Visit site
              </a>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(b)}
              disabled={busy}
              className="text-gray-400 hover:text-red-600 hover:bg-red-50 px-2"
              title="Delete this business"
              data-testid={`admin-delete-${b.business_id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
